// src/auth.js

import { identityApi } from "@/modules/identity/api/identityApi";
import Cookies from "js-cookie";
import router from "@/router";
import store from "@/store";
import { showToast } from "./toastService";
import {
  initializeWebSocket,
  disconnectWebSocket,
  restartWebSocketAuthentication,
} from "@/shared/realtime/websocket";
import { createTokenRefreshHandler } from "@/shared/realtime/createTokenRefreshHandler.mjs";
const expHours = 9 / 24; // 9 godzin
const secureCookie = window.location.protocol === "https:";
const applyRotatedToken = createTokenRefreshHandler({
  setToken: (token) => store.dispatch("setToken", token),
  restartAuthentication: restartWebSocketAuthentication,
});
// Metoda logowania
export const login = async (email, password) => {
  try {
    // Wysłanie żądania logowania do backendu
    const response = await identityApi.login(email, password);
    // Destrukturyzacja odpowiedzi
    const { status, access_token, user } = response.data;

    // Sprawdzenie, czy status jest "ok" oraz czy access_token i user są obecne
    if (
      status === "ok" &&
      access_token &&
      user &&
      user.user_type &&
      user.imie &&
      user.nazwisko
    ) {
      // Zapisanie user_type w ciasteczku
      Cookies.set("user_type", user.user_type, {
        expires: expHours,
        path: "/",
        secure: secureCookie,
        sameSite: "Strict",
      });
      store.dispatch("setToken", access_token);
      // Zapisanie user_type i name w Vuex Store
      store.dispatch("setUser", {
        user_type: user.user_type,
        imie: user.imie,
        nazwisko: user.nazwisko,
        email: user.email,
      });

      // Rozpoczęcie automatycznego odświeżania tokenu
      startTokenRefresh();
      startTokenValidation();
      initializeWebSocket();
      // Dodanie toastu sukcesu
      showToast({
        severity: "success",
        summary: "Sukces",
        detail: "Zalogowano.",
        life: 3000,
      });
      return user;
    } else {
      // Jeśli status nie jest "ok" lub brakuje danych
      throw new Error("Błędny status lub brak wymaganych danych w odpowiedzi.");
    }
  } catch (error) {
    // Obsługa błędów pochodzących z backendu
    if (error.response && error.response.data && error.response.data.message) {
      showToast({
        severity: "error",
        summary: "Błąd",
        detail: `Błąd: ${error.response.data.message}`,
        life: 3000,
      });
    } else {
      // Obsługa błędów sieciowych lub innych
      showToast({
        severity: "error",
        summary: "Błąd",
        detail: "Logowanie nie powiodło się. Spróbuj ponownie.",
        life: 3000,
      });
    }
    return null;
  }
};

// Metoda wylogowania
export const logout = async () => {
  try {
    await identityApi.logout();
  } catch (_error) {
    // 🔹 Wyświetlenie komunikatu o błędzie
    showToast({
      severity: "error",
      summary: "Błąd",
      detail: "Nie udało się wylogować. Spróbuj ponownie.",
      life: 3000,
    });
  }

  stopTokenRefresh(); // Zatrzymanie automatycznego odświeżania tokena
  stopTokenValidation();
  clearAuthState(); // Usunięcie danych użytkownika
  disconnectWebSocket();
  router.push("/");

  showToast({
    severity: "success",
    summary: "Sukces",
    detail: "Wylogowano.",
    life: 3000,
  });
};

// Metoda rejestracji
export const register = async (
  imie,
  nazwisko,
  email,
  password,
  password_confirmation,
  user_type = "user"
) => {
  try {
    // Wysłanie żądania rejestracji do backendu z nowymi polami
    const response = await identityApi.createUser({
      imie,
      nazwisko,
      email,
      password,
      password_confirmation,
      user_type,
    });

    // Powiadomienie o sukcesie
    showToast({
      severity: "success",
      summary: "Sukces",
      detail: response.data.message || "Rejestracja przebiegła pomyślnie.",
      life: 3000,
    });
  } catch (error) {
    if (error.response && error.response.data && error.response.data.message) {
      showToast({
        severity: "error",
        summary: "Błąd",
        detail: `Błąd: ${error.response.data.message}`,
        life: 3000,
      });
    } else if (
      error.response &&
      error.response.data &&
      error.response.data.errors
    ) {
      const errors = Object.values(error.response.data.errors).flat();
      showToast({
        severity: "error",
        summary: "Błędy",
        detail: `Błędy:\n${errors.join("\n")}`,
        life: 3000,
      });
    } else {
      showToast({
        severity: "error",
        summary: "Błąd",
        detail: "Rejestracja nie powiodła się. Spróbuj ponownie.",
        life: 3000,
      });
    }
  }
};

// Metoda pobierania danych użytkownika
export const fetchUser = async () => {
  try {
    const response = await identityApi.currentUser();
    store.dispatch("setUser", response.data);

    Cookies.set("user_type", response.data.user_type, {
      expires: expHours,
      path: "/",
      secure: secureCookie,
      sameSite: "Strict",
    });

    return true;
  } catch (_error) {
    clearAuthState();
    router.push("/");

    showToast({
      severity: "error",
      summary: "Sesja",
      detail: "Sesja wygasła. Zostałeś wylogowany.",
      life: 3000,
    });
    stopTokenRefresh(); // Zatrzymanie automatycznego odświeżania tokena
    stopTokenValidation();
    clearAuthState(); // Usunięcie danych użytkownika
    disconnectWebSocket();
    return false;
  }
};

let tokenValidationInterval = null;

export const startTokenValidation = () => {
  if (tokenValidationInterval) return; // Zapobiega wielokrotnemu ustawieniu interwału

  tokenValidationInterval = setInterval(async () => {
    if (store.getters.isAuthenticated) {
      await fetchUser();
    }
  }, 2 * 60 * 1000); // Co 2 minuty
};

export const stopTokenValidation = () => {
  if (tokenValidationInterval) {
    clearInterval(tokenValidationInterval);
    tokenValidationInterval = null;
  }
};

export const clearAuthState = () => {
  Cookies.remove("auth_token", { path: "/" });
  Cookies.remove("user_type", { path: "/" });
  store.dispatch("clearUser");
};

let refreshTokenInterval = null;

export const startTokenRefresh = () => {
  if (refreshTokenInterval) return; // Unikaj wielokrotnego odświeżania

  refreshTokenInterval = setInterval(async () => {
    try {
      const token = Cookies.get("auth_token");
      if (!token) {
        stopTokenRefresh();
        clearAuthState();
        router.push("/");
        return;
      }

      const response = await identityApi.refreshToken(token);

      await applyRotatedToken(response.data.access_token);
    } catch (_error) {
      showToast({
        severity: "error",
        summary: "Błąd autoryzacji",
        detail: "Wystąpił problem z odświeżeniem tokena. Zaloguj się ponownie.",
        life: 3000,
      });
      stopTokenRefresh();
      clearAuthState();
      router.push("/");
    }
  }, (8 * 60 * 60 + 30 * 60) * 1000); // 8 godzin i 30 minut
};

export const stopTokenRefresh = () => {
  if (refreshTokenInterval) {
    clearInterval(refreshTokenInterval);
    refreshTokenInterval = null;
  }
};
